import React, { useState, useEffect, useMemo } from 'react';

const WelcomeChatbot = ({ inputText, setInputText, handleSendMessage, userId, userName }) => {
  // Query pool - 20+ queries for random selection
  const welcomeQueries = useMemo(() => [
    "What should I focus on to move client work forward today?",
    "Show deadlines that could block delivery this week",
    "Create follow-ups for clients I need to reply to",
    "Plan my deep-work blocks for today",
    "What invoices or admin tasks should I handle next?",
    "Turn my notes into next actions",
    "Summarize meetings and extract action items",
    "Show my calendar and flag conflicts",
    "Set a reminder for proposal follow-up",
    "Draft a client update email",
    "Help me prioritize projects by urgency",
    "Give me a calm plan for the next 3 hours",
    "What did we decide in the last client conversation?",
    "Create a task checklist for a new client onboarding",
    "Review my documents and summarize key points",
    "Search latest trends relevant to my business",
    "Organize my tasks by client",
    "Help me close today without missing anything"
  ], []);

  // Sub-message pool for rotating messages
  const subMessages = useMemo(() => [
    "What feels most overloaded right now? I can take it off your plate.",
    "Let's turn today's chaos into a clear plan.",
    "I can help you protect focus time and keep client work moving.",
    "What do you need done first so the rest of your day feels lighter?",
    "Tell me your top priority — I'll help structure the rest.",
    "Need a calm next-step plan for work and life today?",
    "Let's clear follow-ups, deadlines, and mental clutter.",
    "What's one thing I can organize for you right now?"
  ], []);

  // For API quick actions
  const [apiQuickActions, setApiQuickActions] = useState([]);
  const [apiDescription, setApiDescription] = useState(null);

  // Generate time-based greeting with user's name
  const getTimeBasedGreeting = useMemo(() => {
    const hour = new Date().getHours();
    const name = userName ? `, ${userName}` : '';
    
    if (hour < 12) {
      return `Good morning${name}! ☀️`;
    } else if (hour < 17) {
      return `Good afternoon${name}! 👋`;
    } else {
      return `Good evening${name}! 🌙`;
    }
  }, [userName]);

  // Get a random sub-message - memoized to stay consistent during session
  const randomSubMessage = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * subMessages.length);
    return subMessages[randomIndex];
  }, [subMessages]);

  // Generate 6 random queries from the pool - memoized to prevent re-generation
  const fallbackQueries = useMemo(() => {
    const shuffled = [...welcomeQueries].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 6);
  }, [welcomeQueries]);

  // Get API base URL from environment variable
  const apiBaseUrl = process.env.REACT_APP_ZOOMEY_API_URL || 'https://zoomey-dev.integrowsystems.com';

  // On mount, fetch API quick actions in background (non-blocking)
  useEffect(() => {
    if (!userId) return;

    // Use AbortController for cleanup
    const controller = new AbortController();
    
    // Fetch in background - don't block UI
    fetch(`${apiBaseUrl}get_suggestions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
      signal: controller.signal
    })
      .then(res => {
        if (!res.ok) throw new Error('API error');
        return res.json();
      })
      .then(data => {
        if (data?.quick_search && Array.isArray(data.quick_search)) {
          setApiQuickActions(data.quick_search);
          // Use API description as sub-message if available
          if (data.short_description && data.short_description.length > 5) {
            setApiDescription(data.short_description);
          }
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch suggestions:', error);
        }
      });

    return () => controller.abort();
  }, [userId, apiBaseUrl]);

  // Always use personalized time-based greeting with user's name
  // API description can enhance the sub-message if available
  const displayGreeting = getTimeBasedGreeting;
  const displaySubMessage = apiDescription || randomSubMessage;

  // Handle quick action button clicks
  const handleQuickAction = (query) => {
    setInputText(query);
  };

  return (
    <div className="welcome-interface">
      <div className="text-center mb-5">
        <h1 className="welcome-title display-3 fw-bold mb-3">
          {displayGreeting}
        </h1>
        <p className="welcome-subtitle fs-4 text-muted mb-0">
          {displaySubMessage}
        </p>
      </div>

      <div className="container-fluid px-4">
        <div className="d-flex flex-wrap justify-content-center gap-3">
          {apiQuickActions.length > 0 ? (
            // API-loaded suggestions
            apiQuickActions.map((item, idx) => (
              <div key={idx}>
                <button
                  onClick={() => handleQuickAction(item.query)}
                  className="welcome-query-btn"
                >
                  {item.icon && <span className="me-2">{item.icon}</span>}
                  {item.title}
                </button>
              </div>
            ))
          ) : (
            // Fallback suggestions (shown immediately)
            fallbackQueries.map((query, index) => (
              <div key={index}>
                <button
                  onClick={() => handleQuickAction(query)}
                  className="welcome-query-btn"
                >
                  {query}
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="text-center mt-5">
        <p className="mb-3" style={{color:"var(--text)"}}>
          Type <span className="text-primary fw-bold">@</span> to instantly offload actions
        </p>
      </div>
    </div>
  );
};

export default WelcomeChatbot;
